import { useEffect, useMemo, useState } from 'react'
import Papa from 'papaparse'
import * as XLSX from 'xlsx'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useHomeStore } from '../store'
import type { ParseXlsxStandard } from '../api'

interface Props {
  file: File | null
  parsed?: ParseXlsxStandard | null
  onClose: () => void
  onImported: (carIds: number[]) => void
}

interface ParsedTable {
  headers: string[]
  rows: Record<string, unknown>[]
}

type FieldKey = 'vin' | 'spz' | 'najete_km' | 'hodnota_vozidla'

const FIELDS: { key: FieldKey; label: string; required?: boolean }[] = [
  { key: 'vin', label: 'VIN', required: true },
  { key: 'spz', label: 'SPZ' },
  { key: 'najete_km', label: 'Najeté km' },
  { key: 'hodnota_vozidla', label: 'Hodnota vozidla' },
]

const VIN_RE = /^[A-HJ-NPR-Z0-9]{7,17}$/i

export function BulkImportModal({ file, parsed: serverParsed, onClose, onImported }: Props) {
  const [parsed, setParsed] = useState<ParsedTable | null>(null)
  const [mapping, setMapping] = useState<Record<FieldKey, string>>({} as Record<FieldKey, string>)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const addVehicles = useHomeStore((s) => s.addVehicles)
  const hasVin = useHomeStore((s) => s.hasVin)
  const updateVehicle = useHomeStore((s) => s.updateVehicle)

  useEffect(() => {
    let cancelled = false
    if (!file) {
      setParsed(null)
      setMapping({} as Record<FieldKey, string>)
      setError(null)
      return
    }
    if (serverParsed) {
      const data: ParsedTable = { headers: serverParsed.columns, rows: serverParsed.rows }
      setParsed(data)
      const auto = autoDetect(data.headers)
      if (serverParsed.vinColumnHint && data.headers.includes(serverParsed.vinColumnHint)) {
        auto.vin = serverParsed.vinColumnHint
      }
      setMapping(auto)
      return
    }
    void (async () => {
      try {
        const data = await parseFile(file)
        if (cancelled) return
        setParsed(data)
        setMapping(autoDetect(data.headers))
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Chyba při čtení souboru')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [file, serverParsed])

  const preview = useMemo(() => parsed?.rows.slice(0, 3) ?? [], [parsed])

  function submit() {
    if (!parsed) return
    setError(null)
    setBusy(true)
    try {
      const vinCol = mapping.vin
      if (!vinCol) {
        setError('Vyberte sloupec VIN.')
        return
      }
      const accepted: { vin: string; row: Record<string, unknown> }[] = []
      const invalid: string[] = []
      for (const row of parsed.rows) {
        const vinRaw = String(row[vinCol] ?? '').trim().toUpperCase()
        if (!vinRaw) continue
        if (!VIN_RE.test(vinRaw)) {
          invalid.push(vinRaw)
          continue
        }
        if (hasVin(vinRaw)) continue
        accepted.push({ vin: vinRaw, row })
      }
      const ids = addVehicles(accepted.map((a) => ({ vin: a.vin })))
      for (let i = 0; i < ids.length; i++) {
        const patch: Record<string, string> = {}
        const row = accepted[i].row
        if (mapping.spz) patch.spz = String(row[mapping.spz] ?? '')
        if (mapping.najete_km) patch.najete_km = String(row[mapping.najete_km] ?? '')
        if (mapping.hodnota_vozidla) patch.hodnota_vozidla = String(row[mapping.hodnota_vozidla] ?? '')
        if (Object.keys(patch).length > 0) updateVehicle(ids[i], patch)
      }
      onImported(ids)
      if (invalid.length > 0) {
        setError(`Importováno ${ids.length} vozidel. Přeskočeno neplatných: ${invalid.length}.`)
      } else {
        onClose()
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={!!file} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Vyberte sloupce z Excel/CSV</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-zinc-600">
          Vyberte, které sloupce obsahují jaká data. Soubor: <strong>{file?.name}</strong>
        </p>

        {!parsed && !error && <p className="text-sm text-zinc-500">Čtu soubor…</p>}
        {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

        {parsed && (
          <>
            <div className="space-y-2">
              {FIELDS.map((f) => (
                <div key={f.key} className="grid grid-cols-3 items-center gap-3">
                  <span className="text-sm font-medium">
                    {f.label} {f.required && <span className="text-red-500">*</span>}
                  </span>
                  <select
                    value={mapping[f.key] ?? ''}
                    onChange={(e) =>
                      setMapping((m) => ({ ...m, [f.key]: e.target.value }))
                    }
                    className="col-span-2 h-9 rounded-md border border-zinc-300 bg-white px-2 text-sm"
                  >
                    <option value="">— vynechat —</option>
                    {parsed.headers.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            <div className="rounded-md bg-zinc-50 p-3 text-xs">
              <p className="mb-2 font-medium">Náhled prvních řádků:</p>
              <div className="overflow-x-auto">
                <table className="min-w-full text-left">
                  <thead>
                    <tr>
                      {parsed.headers.map((h) => (
                        <th key={h} className="border-b border-zinc-200 px-2 py-1 font-medium">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((r, i) => (
                      <tr key={i}>
                        {parsed.headers.map((h) => (
                          <td key={h} className="border-b border-zinc-100 px-2 py-1">
                            {String(r[h] ?? '')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Zrušit
          </Button>
          <Button onClick={submit} disabled={!parsed || busy}>
            {busy ? 'Importuji…' : 'Importovat data'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

async function parseFile(file: File): Promise<ParsedTable> {
  const name = file.name.toLowerCase()
  if (name.endsWith('.csv')) {
    const text = await file.text()
    const res = Papa.parse<Record<string, unknown>>(text, { header: true, skipEmptyLines: true })
    const headers = res.meta.fields ?? []
    return { headers, rows: (res.data as Record<string, unknown>[]).filter((r) => Object.keys(r).length > 0) }
  }
  const buf = await file.arrayBuffer()
  const wb = XLSX.read(buf, { type: 'array' })
  const sheet = wb.Sheets[wb.SheetNames[0]]
  const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' })
  const headers = rows.length > 0 ? Object.keys(rows[0]) : []
  return { headers, rows }
}

function autoDetect(headers: string[]): Record<FieldKey, string> {
  const m: Partial<Record<FieldKey, string>> = {}
  for (const h of headers) {
    const lower = h.toLowerCase()
    if (!m.vin && /vin/.test(lower)) m.vin = h
    if (!m.spz && /(spz|rz|registra)/.test(lower)) m.spz = h
    if (!m.najete_km && /(km|nájezd|najet)/.test(lower)) m.najete_km = h
    if (!m.hodnota_vozidla && /(hodnota|cena|value)/.test(lower)) m.hodnota_vozidla = h
  }
  return m as Record<FieldKey, string>
}
